import { ApiError } from "../api/http";

export type AdaptivePollingKind = "analysis" | "ai-report";

export type PollResponseState<T> =
  | { type: "pending" }
  | { type: "completed"; value: T }
  | { type: "failed"; error: Error };

export type AdaptivePollingOutcome<T> =
  | { type: "completed"; value: T }
  | { type: "failed"; error: Error }
  | { type: "observation-window-ended" };

type AdaptivePollingOptions<TResponse, TValue> = {
  kind: AdaptivePollingKind;
  poll: (signal: AbortSignal) => Promise<TResponse>;
  evaluate: (response: TResponse) => PollResponseState<TValue>;
  signal: AbortSignal;
  now?: () => number;
  random?: () => number;
};

const observationWindowMs = 30 * 60 * 1000;
const transientErrorDelaysMs = [2_000, 4_000, 8_000, 16_000, 30_000] as const;

export async function runAdaptivePolling<TResponse, TValue>({
  kind,
  poll,
  evaluate,
  signal,
  now = monotonicNow,
  random = Math.random,
}: AdaptivePollingOptions<TResponse, TValue>): Promise<AdaptivePollingOutcome<TValue>> {
  const startedAt = now();
  let consecutiveErrors = 0;

  while (true) {
    throwIfAborted(signal);
    await waitUntilPollable(signal);

    const elapsedBeforePoll = Math.max(0, now() - startedAt);
    const isFinalPoll = elapsedBeforePoll >= observationWindowMs;

    try {
      const response = await poll(signal);
      throwIfAborted(signal);
      consecutiveErrors = 0;

      const state = evaluate(response);
      if (state.type === "completed" || state.type === "failed") {
        return state;
      }
    } catch (error) {
      throwIfAborted(signal);
      if (!isTransientPollingError(error)) {
        throw error;
      }
      consecutiveErrors += 1;
    }

    const elapsed = Math.max(0, now() - startedAt);
    if (isFinalPoll || elapsed >= observationWindowMs) {
      return { type: "observation-window-ended" };
    }

    const baseDelay = pollingIntervalMs(kind, elapsed);
    const errorDelay = consecutiveErrors > 0
      ? transientErrorDelaysMs[Math.min(consecutiveErrors - 1, transientErrorDelaysMs.length - 1)]
      : 0;
    const delayMs = Math.min(
      jitteredDelay(Math.max(baseDelay, errorDelay), random),
      observationWindowMs - elapsed,
    );
    await waitForNextPoll(delayMs, signal);
  }
}

export function pollingIntervalMs(kind: AdaptivePollingKind, elapsedMs: number): number {
  if (kind === "ai-report") {
    if (elapsedMs < 2 * 60 * 1000) return 5_000;
    if (elapsedMs < 10 * 60 * 1000) return 10_000;
    return 30_000;
  }

  if (elapsedMs < 30_000) return 2_000;
  if (elapsedMs < 2 * 60 * 1000) return 5_000;
  if (elapsedMs < 10 * 60 * 1000) return 10_000;
  return 30_000;
}

export function isTransientPollingError(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  return error.status === 0
    || error.status === 408
    || error.status === 429
    || error.status >= 500;
}

function jitteredDelay(baseDelayMs: number, random: () => number): number {
  const normalizedRandom = Math.min(Math.max(random(), 0), 1);
  return Math.round(baseDelayMs * (0.8 + normalizedRandom * 0.4));
}

function monotonicNow(): number {
  return typeof performance === "undefined" ? Date.now() : performance.now();
}

function canPoll(): boolean {
  const isVisible = typeof document === "undefined" || document.visibilityState !== "hidden";
  const isOnline = typeof navigator === "undefined" || navigator.onLine !== false;
  return isVisible && isOnline;
}

async function waitUntilPollable(signal: AbortSignal): Promise<void> {
  while (!canPoll()) {
    await new Promise<void>((resolve, reject) => {
      const finishIfReady = () => {
        if (!canPoll()) return;
        cleanup();
        resolve();
      };
      const onAbort = () => {
        cleanup();
        reject(abortError());
      };
      const cleanup = () => {
        document.removeEventListener("visibilitychange", finishIfReady);
        window.removeEventListener("online", finishIfReady);
        signal.removeEventListener("abort", onAbort);
      };

      document.addEventListener("visibilitychange", finishIfReady);
      window.addEventListener("online", finishIfReady);
      signal.addEventListener("abort", onAbort, { once: true });
      finishIfReady();
    });
    throwIfAborted(signal);
  }
}

async function waitForNextPoll(delayMs: number, signal: AbortSignal): Promise<void> {
  if (!canPoll()) {
    await waitUntilPollable(signal);
    return;
  }

  const interruptedByPause = await new Promise<boolean>((resolve, reject) => {
    const timer = window.setTimeout(() => finish(false), Math.max(0, delayMs));
    const onAvailabilityChange = () => {
      if (!canPoll()) finish(true);
    };
    const onAbort = () => {
      cleanup();
      reject(abortError());
    };
    const cleanup = () => {
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", onAvailabilityChange);
      window.removeEventListener("offline", onAvailabilityChange);
      signal.removeEventListener("abort", onAbort);
    };
    const finish = (wasPaused: boolean) => {
      cleanup();
      resolve(wasPaused);
    };

    document.addEventListener("visibilitychange", onAvailabilityChange);
    window.addEventListener("offline", onAvailabilityChange);
    signal.addEventListener("abort", onAbort, { once: true });
  });

  if (interruptedByPause) {
    await waitUntilPollable(signal);
  }
}

function throwIfAborted(signal: AbortSignal): void {
  if (signal.aborted) throw abortError();
}

function abortError(): DOMException {
  return new DOMException("Polling cancelled", "AbortError");
}
